import { decodeHtmlEntities } from "@/utilities/decodeHtmlEntities";

export type LayoutBlock = Record<string, unknown> & {
  blockName?: string | null;
  previewAdminTarget?: string;
  blockType?: string;
};

const VALID_HTML_ID = /^[a-z][\w-]*$/;

/** å/ä → a, ö → o (Swedish block names in CMS). */
export function normalizeBlockNameForAnchor(value: string): string {
  return value.replace(/[åäÅÄ]/g, "a").replace(/[öÖ]/g, "o");
}

/** Payload blockName → safe lowercase `id` for `#anchor` links. */
export function blockNameToAnchorId(
  blockName: string | null | undefined,
): string | undefined {
  const trimmed = typeof blockName === "string" ? blockName.trim() : "";
  if (!trimmed) return undefined;

  const normalized = normalizeBlockNameForAnchor(trimmed).toLowerCase();

  if (VALID_HTML_ID.test(normalized)) return normalized;

  const slug = normalized
    .replace(/[^a-z0-9\s-_]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");

  return slug.length > 0 ? slug : undefined;
}

export const asString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;

  const decoded = decodeHtmlEntities(value);
  return decoded && decoded.trim().length > 0 ? decoded : undefined;
};

export const asBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

export const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

export const richTextToPlainText = (value: unknown): string | undefined => {
  if (typeof value === "string") return asString(value);
  if (!(typeof value === "object" && value)) return undefined;

  const root = "root" in value ? (value as { root?: unknown }).root : undefined;
  if (!(typeof root === "object" && root)) return undefined;

  const collectText = (node: unknown): string[] => {
    if (!(typeof node === "object" && node)) return [];

    const maybeText =
      "text" in node ? (node as { text?: unknown }).text : undefined;
    const currentText =
      typeof maybeText === "string"
        ? [decodeHtmlEntities(maybeText) ?? maybeText]
        : [];

    const children =
      "children" in node
        ? (node as { children?: unknown }).children
        : undefined;
    if (!Array.isArray(children)) return currentText;

    return [...currentText, ...children.flatMap((child) => collectText(child))];
  };

  const lines = collectText(root)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length ? lines.join(" ") : undefined;
};
